import api from './api';
import {ApiResponse} from '../types/common';
import {
    CreateStrategyTaskRequest,
    StrategyTask,
    StrategyTaskDetail,
    TradeRecord,
    UpdateStrategyTaskRequest
} from '../types/strategyTask';

/**
 * 策略任务API服务
 * 提供策略任务的CRUD、状态管理、统计数据查询等接口调用
 */
export const strategyTaskService = {
    /**
     * 获取所有策略任务
     */
    getAllTasks: async (): Promise<StrategyTask[]> => {
        const response = await api.get<ApiResponse<StrategyTask[]>>('/api/v1/strategy-tasks');
        return response.data.data || [];
    },

    /**
     * 获取运行中的策略任务
     */
    getRunningTasks: async (): Promise<StrategyTask[]> => {
        const response = await api.get<ApiResponse<StrategyTask[]>>('/api/v1/strategy-tasks/running');
        return response.data.data || [];
    },

    /**
     * 根据ID获取任务详情
     */
    getTaskById: async (id: number): Promise<StrategyTask | null> => {
        const response = await api.get<ApiResponse<StrategyTask>>(`/api/v1/strategy-tasks/${id}`);
        return response.data.data || null;
    },

    /**
     * 创建策略任务
     */
    createTask: async (task: CreateStrategyTaskRequest): Promise<StrategyTask | null> => {
        const response = await api.post<ApiResponse<StrategyTask>>('/api/v1/strategy-tasks', task);
        return response.data.data || null;
    },

    /**
     * 更新策略任务
     */
    updateTask: async (id: number, task: UpdateStrategyTaskRequest): Promise<StrategyTask | null> => {
        const response = await api.put<ApiResponse<StrategyTask>>(`/api/v1/strategy-tasks/${id}`, task);
        return response.data.data || null;
    },

    /**
     * 删除策略任务
     */
    deleteTask: async (id: number): Promise<boolean> => {
        const response = await api.delete<ApiResponse<void>>(`/api/v1/strategy-tasks/${id}`);
        return response.data.code === '0';
    },

    /**
     * 暂停策略任务
     */
    pauseTask: async (id: number): Promise<StrategyTask | null> => {
        const response = await api.put<ApiResponse<StrategyTask>>(`/api/v1/strategy-tasks/${id}/pause`);
        return response.data.data || null;
    },

    /**
     * 启动策略任务
     */
    startTask: async (id: number): Promise<StrategyTask | null> => {
        const response = await api.post<ApiResponse<StrategyTask>>(`/api/v1/strategy-tasks/${id}/start`);
        return response.data.data || null;
    },

    /**
     * 停止策略任务
     */
    stopTask: async (id: number): Promise<StrategyTask | null> => {
        const response = await api.post<ApiResponse<StrategyTask>>(`/api/v1/strategy-tasks/${id}/stop`);
        return response.data.data || null;
    },

    /**
     * 恢复策略任务
     */
    resumeTask: async (id: number): Promise<StrategyTask | null> => {
        const response = await api.put<ApiResponse<StrategyTask>>(`/api/v1/strategy-tasks/${id}/resume`);
        return response.data.data || null;
    },

    /**
     * 重启策略任务
     */
    restartTask: async (id: number): Promise<StrategyTask | null> => {
        const response = await api.post<ApiResponse<StrategyTask>>(`/api/v1/strategy-tasks/${id}/restart`);
        return response.data.data || null;
    },

    /**
     * 暂停所有策略任务
     */
    pauseAllTasks: async (): Promise<number> => {
        const response = await api.put<ApiResponse<number>>('/api/v1/strategy-tasks/pause-all');
        return response.data.data || 0;
    },

    /**
     * 获取任务统计数据
     */
    getTaskStats: async (id: number): Promise<StrategyTask | null> => {
        const response = await api.get<ApiResponse<StrategyTask>>(`/api/v1/strategy-tasks/${id}/stats`);
        return response.data.data || null;
    },

    /**
     * 获取任务聚合详情
     */
    getTaskDetail: async (id: number, tradeLimit = 10, logTail = 80): Promise<StrategyTaskDetail | null> => {
        const response = await api.get<ApiResponse<StrategyTaskDetail>>(`/api/v1/strategy-tasks/${id}/detail`, {
            params: {tradeLimit, logTail}
        });
        return response.data.data || null;
    },

    /**
     * 获取任务的成交记录
     */
    getTaskTrades: async (id: number, page = 0, size = 20): Promise<any> => {
        const response = await api.get<any>(`/api/v1/strategy-tasks/${id}/trades`, {
            params: {page, size}
        });
        return response.data.data || {content: [], totalElements: 0};
    },

    /**
     * 获取用户最新成交记录
     */
    getRecentTrades: async (limit = 10): Promise<TradeRecord[]> => {
        const response = await api.get<ApiResponse<TradeRecord[]>>('/api/v1/strategy-tasks/recent-trades', {
            params: {limit}
        });
        return response.data.data || [];
    }
};
